/**
 * Unified Media Upload Modal
 * Standardized modal for uploading media with smart single/bulk detection
 */

import { useState, useEffect, useCallback } from "react";
import { Modal, Upload, Form, Input, List, Tag, message, Button } from "antd";
import { InboxOutlined, DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  validateFileSize,
  validateImageDimensions,
  validateVideoDuration,
  getImageDimensions,
  getVideoMetadata,
  uploadToS3Storage,
  getFileNameWithoutExtension,
  capitalizeFirstLetter,
  MEDIA_CONSTANTS,
} from "../utils/mediaUtils";

const { Dragger } = Upload;

const MediaUploadModal = ({
  visible,
  onCancel,
  onUpload,
  mediaType = "IMAGE",
  title = "Upload Media",
  validDimensions = [],
  loading = false,
}) => {
  const [uploadState, setUploadState] = useState({
    mode: "idle", // 'idle', 'single', 'bulk'
    files: [],
    validationResults: [],
    errors: [],
    currentFile: null,
    previewUrl: null,
    previewDimensions: null,
    key: null,
  });

  const [form] = Form.useForm();
  const [showRequirements, setShowRequirements] = useState(false);
  const [isUploadingPreview, setIsUploadingPreview] = useState(false);

  const clearState = useCallback(() => {
    setUploadState({
      mode: "idle",
      files: [],
      validationResults: [],
      errors: [],
      currentFile: null,
      previewUrl: null,
      previewDimensions: null,
      key: null,
    });
    form.resetFields();
  }, [form]);

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      clearState();
    }
  }, [clearState, visible]);

  const validateFile = async file => {
    const errors = [];

    // Check file size
    if (!validateFileSize(file, MEDIA_CONSTANTS.MAX_FILE_SIZE_MB)) {
      errors.push(
        `File size must be less than ${MEDIA_CONSTANTS.MAX_FILE_SIZE_MB}MB`
      );
    }

    // Check file type
    const allowedTypes =
      mediaType === "VIDEO"
        ? MEDIA_CONSTANTS.ALLOWED_VIDEO_TYPES
        : MEDIA_CONSTANTS.ALLOWED_IMAGE_TYPES;

    if (!allowedTypes.includes(file.type)) {
      const fileExtension = file.name.split('.').pop().toUpperCase();
      const allowedExtensions = mediaType === "VIDEO" ? ["MP4"] : ["PNG", "JPG", "JPEG", "GIF"];
      errors.push(
        `"${file.name}" is not a supported file type. ` +
        `${fileExtension} files are not allowed. ` +
        `Please upload ${mediaType === "VIDEO" ? "video" : "image"} files only: ${allowedExtensions.join(", ")}`
      );
    }

    try {
      let dimensions = { width: 0, height: 0 };
      let duration = 0;

      if (mediaType === "VIDEO") {
        // Use getVideoMetadata to get full metadata including bitrate validation
        const metadata = await getVideoMetadata(file);

        duration = metadata.duration;
        dimensions = { width: metadata.width, height: metadata.height };

        // Check duration
        if (!validateVideoDuration(duration, MEDIA_CONSTANTS.VIDEO_DURATION_MIN, MEDIA_CONSTANTS.VIDEO_DURATION_MAX)) {
          errors.push(
            `Video duration must be between ${MEDIA_CONSTANTS.VIDEO_DURATION_MIN}-${MEDIA_CONSTANTS.VIDEO_DURATION_MAX} seconds`
          );
        }

        // Check for critical bitrate issues (marked with ❌)
        // This ensures files with high/low bitrate are marked as invalid immediately
        if (metadata.hasIssues && metadata.issues && metadata.issues.length > 0) {
          metadata.issues.forEach(issue => {
            if (issue.startsWith('❌')) {
              // Remove the ❌ emoji for cleaner error message
              errors.push(issue.replace('❌ ', ''));
            }
          });
        }
      } else {
        dimensions = await getImageDimensions(file);

        if (
          validDimensions.length > 0 &&
          !validateImageDimensions(
            dimensions.width,
            dimensions.height,
            validDimensions
          )
        ) {
          errors.push(
            `Invalid dimensions. Allowed: ${validDimensions
              .map(d => `${d.width}x${d.height}`)
              .join(", ")}`
          );
        }
      }

      return {
        valid: errors.length === 0,
        errors,
        duration,
        dimensions,
        file,
      };
    } catch (error) {
      errors.push("Failed to analyze file");
      return {
        valid: false,
        errors,
        duration: 0,
        dimensions: { width: 0, height: 0 },
        file,
      };
    }
  };

  const handleFileSelection = async info => {
    const files = info.fileList.map(
      fileInfo => fileInfo.originFileObj || fileInfo
    );

    if (files.length === 0) {
      setUploadState(prev => ({ ...prev, mode: "idle", files: [] }));
      return;
    }

    // Validate all files
    const validationResults = await Promise.all(
      files.map(file => validateFile(file))
    );

    const mode = files.length === 1 ? "single" : "bulk";

    setUploadState(prev => ({
      ...prev,
      mode,
      files,
      validationResults,
      errors: [],
      currentFile: files.length === 1 ? files[0] : null,
      previewUrl: null,
      previewDimensions: null,
      key: null,
    }));

    // For single file mode, set up preview if valid
    if (mode === "single" && validationResults[0]?.valid) {
      await setupPreview(files[0], validationResults[0]);
    }
  };

  const setupPreview = async (file, validationResult) => {
    setIsUploadingPreview(true);
    try {
      const contentType = mediaType === "VIDEO" ? "video/mp4" : "image/jpeg";
      const response = await uploadToS3Storage(file, contentType);

      setUploadState(prev => ({
        ...prev,
        previewUrl: response.url,
        previewDimensions: validationResult.dimensions,
        key: response.key,
      }));

      // Auto-fill form name
      if (!form.getFieldValue("name")) {
        const fileName = getFileNameWithoutExtension(file.name);
        form.setFieldsValue({ name: fileName });
      }
    } catch (error) {
      message.error("Failed to upload file for preview");
    } finally {
      setIsUploadingPreview(false);
    }
  };

  const removeFile = index => {
    const newFiles = uploadState.files.filter((_, i) => i !== index);
    const newValidationResults = uploadState.validationResults.filter(
      (_, i) => i !== index
    );

    const newMode =
      newFiles.length === 0
        ? "idle"
        : newFiles.length === 1
        ? "single"
        : "bulk";

    setUploadState(prev => ({
      ...prev,
      mode: newMode,
      files: newFiles,
      validationResults: newValidationResults,
    }));

    if (newMode === "single" && newValidationResults[0]?.valid) {
      setupPreview(newFiles[0], newValidationResults[0]);
    }
  };

  const handleSingleUpload = async values => {
    const {
      validationResults,
      key: uploadKey,
      previewUrl,
      previewDimensions,
    } = uploadState;

    if (!uploadKey || !previewUrl) {
      message.error("Please select a file to upload");
      return;
    }

    if (validationResults.length === 0 || !validationResults[0].valid) {
      message.error("Please upload a valid file");
      return;
    }

    const { file } = validationResults[0];
    const fileName = values.name;
    const capitalizedName = capitalizeFirstLetter(fileName);

    const mediaData = {
      mediatype: mediaType,
      key: uploadKey,
      url: previewUrl,
      width: previewDimensions.width,
      height: previewDimensions.height,
      name: capitalizedName,
      size: file.size,
      fileType: file.type.split("/")[1].toUpperCase(),
    };

    onUpload([mediaData], "single");
  };

  const handleBulkUpload = () => {
    const { validationResults } = uploadState;
    const validFiles = validationResults.filter(result => result.valid);

    if (validFiles.length === 0) {
      message.error("No valid files to upload");
      return;
    }

    const mediaData = validFiles.map(({ file, dimensions }) => ({
      file,
      dimensions,
      name: getFileNameWithoutExtension(file.name),
    }));

    onUpload(mediaData, "bulk");
  };

  const renderContent = () => {
    const { mode, files, validationResults } = uploadState;

    const handleBeforeUpload = (file) => {
      // Quick file type check for immediate feedback
      const allowedTypes = mediaType === "VIDEO"
        ? ["video/mp4"]
        : ["image/png", "image/jpeg", "image/jpg", "image/gif"];
      
      if (!allowedTypes.includes(file.type)) {
        const fileExtension = file.name.split('.').pop().toUpperCase();
        const allowedExtensions = mediaType === "VIDEO" ? ["MP4"] : ["PNG", "JPG", "JPEG", "GIF"];
        message.error(
          `"${file.name}" is not supported. ${fileExtension} files are not allowed. ` +
          `Please select ${mediaType === "VIDEO" ? "video" : "image"} files only: ${allowedExtensions.join(", ")}`,
          6
        );
      }
      return false; // Prevent auto upload
    };

    const dragProps = {
      multiple: true,
      accept: mediaType === "VIDEO" 
        ? ".mp4,video/mp4" 
        : ".png,.jpg,.jpeg,.gif,image/png,image/jpeg,image/gif",
      showUploadList: false,
      beforeUpload: handleBeforeUpload,
      onChange: handleFileSelection,
    };

    if (mode === "single") {
      return (
        <div>
          <Dragger {...dragProps} style={{ padding: '20px 0' }}>
            <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
              <InboxOutlined style={{ fontSize: 36 }} />
            </p>
            <p className="ant-upload-text" style={{ marginBottom: 4, fontSize: 14 }}>
              Drop {mediaType.toLowerCase()} file here or click to browse
            </p>
            <p className="ant-upload-hint" style={{ margin: 0, fontSize: 12 }}>
              {mediaType === "VIDEO"
                ? "MP4 format, 6-30 seconds, max 30MB"
                : "PNG, JPG, JPEG, or GIF format"}
              <br />
              💡 Hold Ctrl/Cmd to select multiple files for bulk upload
            </p>
          </Dragger>

          {uploadState.previewUrl && (
            <div style={{ marginTop: 16 }}>
              {mediaType === "VIDEO" ? (
                <video controls width="100%" height="auto">
                  <source src={uploadState.previewUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={uploadState.previewUrl}
                  alt="Preview"
                  style={{
                    width: "100%",
                    maxHeight: "300px",
                    objectFit: "contain",
                  }}
                />
              )}
            </div>
          )}

          {validationResults.length > 0 && !validationResults[0].valid && (
            <div style={{ marginTop: 16 }}>
              {validationResults[0].errors.map((error, index) => (
                <Tag
                  key={index}
                  color="red"
                  style={{
                    marginBottom: 4,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    display: 'inline-block',
                    maxWidth: '100%'
                  }}
                >
                  {error}
                </Tag>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (mode === "bulk") {
      return (
        <div>
          <Dragger {...dragProps} style={{ padding: '20px 0' }}>
            <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
              <InboxOutlined style={{ fontSize: 36 }} />
            </p>
            <p className="ant-upload-text" style={{ marginBottom: 4, fontSize: 14 }}>
              Drop {mediaType.toLowerCase()} files here or click to browse
            </p>
            <p className="ant-upload-hint" style={{ margin: 0, fontSize: 12 }}>
              {files.length} files selected
              <br />
              {mediaType === "VIDEO"
                ? "MP4 format, max 30MB per file"
                : "PNG, JPG, JPEG, or GIF format"}
            </p>
          </Dragger>

          <List
            style={{ marginTop: 16 }}
            size="small"
            dataSource={files}
            renderItem={(file, index) => {
              const validation = validationResults[index];
              return (
                <List.Item
                  actions={[
                    <DeleteOutlined
                      key="remove"
                      onClick={() => removeFile(index)}
                      style={{ color: "#ff4d4f", cursor: "pointer" }}
                    />,
                  ]}
                >
                  <List.Item.Meta
                    title={file.name}
                    description={
                      <div>
                        <div>
                          Size: {(file.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                        {validation && (
                          <div>
                            {validation.valid ? (
                              <Tag color="green">Valid</Tag>
                            ) : (
                              <div>
                                {validation.errors.map((error, errorIndex) => (
                                  <Tag
                                    key={errorIndex}
                                    color="red"
                                    style={{
                                      marginBottom: 2,
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word',
                                      display: 'inline-block',
                                      maxWidth: '100%'
                                    }}
                                  >
                                    {error}
                                  </Tag>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </div>
      );
    }

    return (
      <Dragger {...dragProps} style={{ padding: '20px 0' }}>
        <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
          <InboxOutlined style={{ fontSize: 36 }} />
        </p>
        <p className="ant-upload-text" style={{ marginBottom: 4, fontSize: 14 }}>
          Drop {mediaType.toLowerCase()} file(s) here or click to browse
        </p>
        <p className="ant-upload-hint" style={{ margin: 0, fontSize: 12 }}>
          {mediaType === "VIDEO"
            ? "MP4 format, 6-30 seconds, max 30MB per file"
            : "PNG, JPG, JPEG, or GIF format"}
          <br />
          💡 Select multiple files at once using Ctrl/Cmd + Click
        </p>
      </Dragger>
    );
  };

  const getModalTitle = () => {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: '-4px 0',
        paddingRight: '24px'
      }}>
        <span>{title}</span>
        <Button
          type="text"
          size="small"
          icon={<InfoCircleOutlined />}
          onClick={() => setShowRequirements(true)}
          style={{
            marginLeft: '16px',
            padding: '0 8px',
            height: '24px',
            fontSize: '12px'
          }}
        >
          Requirements
        </Button>
      </div>
    );
  };

  const getFooter = () => {
    const { mode, validationResults } = uploadState;

    // Single file upload
    if (mode === "single") {
      const isValid =
        validationResults.length > 0 && validationResults[0].valid;
      return [
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          disabled={!isValid || loading || isUploadingPreview}
          loading={loading || isUploadingPreview}
          onClick={() => form.submit()}
        >
          Upload {mediaType}
        </Button>,
      ];
    }

    // Bulk file upload
    if (mode === "bulk") {
      const validFiles = validationResults.filter(result => result.valid);
      const hasValidFiles = validFiles.length > 0;

      return [
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          disabled={!hasValidFiles || loading || isUploadingPreview}
          loading={loading || isUploadingPreview}
          onClick={handleBulkUpload}
        >
          Upload {validFiles.length} File{validFiles.length !== 1 ? "s" : ""}
        </Button>,
      ];
    }

    // Idle mode - no files selected yet
    return [
      <Button key="cancel" onClick={onCancel}>
        Cancel
      </Button>,
      <Button
        key="upload"
        type="primary"
        disabled={true}
        onClick={() => {}}
      >
        Upload {mediaType}
      </Button>,
    ];
  };

  return (
    <>
      {/* Requirements Modal */}
      <Modal
        title={mediaType === "VIDEO" ? "Video Upload Requirements" : "Image Upload Requirements"}
        open={showRequirements}
        onCancel={() => setShowRequirements(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowRequirements(false)}>
            Got it
          </Button>
        ]}
        width={600}
        centered
      >
        <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
          {mediaType === "VIDEO" ? (
            <>
              <h4 style={{ marginTop: 0 }}>Format & Technical Specifications</h4>
              <ul style={{ paddingLeft: '20px' }}>
                <li><strong>Format:</strong> MP4 only (H.264 codec required)</li>
                <li><strong>Duration:</strong> 6-30 seconds (15-30s recommended for best performance)</li>
                <li><strong>File Size:</strong> Maximum 30MB per file</li>
              </ul>

              <h4>Video Quality</h4>
              <ul style={{ paddingLeft: '20px' }}>
                <li><strong>Resolution:</strong> 1920×1080 (Full HD) or 1280×720 (HD) preferred</li>
                <li><strong>Aspect Ratio:</strong> 16:9 (widescreen) or 4:3 (standard)</li>
                <li><strong>Note:</strong> CTV (Connected TV) campaigns require 16:9 format</li>
              </ul>

              <h4>Bitrate Guidelines</h4>
              <ul style={{ paddingLeft: '20px' }}>
                <li><strong>Required Range:</strong> 800-5000 kbps</li>
                <li><strong>Optimal Range:</strong> 1000-3000 kbps (recommended for best compatibility)</li>
                <li><strong>Note:</strong> Higher bitrates provide better quality but larger file sizes</li>
              </ul>

              <h4>Upload Tips</h4>
              <ul style={{ paddingLeft: '20px' }}>
                <li>Videos are automatically validated during upload</li>
                <li>Warnings will appear for files outside optimal ranges</li>
                <li>You can select multiple files at once for bulk upload (Ctrl/Cmd + Click)</li>
                <li>Invalid files will be highlighted with specific error messages</li>
              </ul>
            </>
          ) : (
            <>
              <h4 style={{ marginTop: 0 }}>Supported Formats</h4>
              <ul style={{ paddingLeft: '20px' }}>
                <li><strong>PNG:</strong> Best for graphics with transparency</li>
                <li><strong>JPG/JPEG:</strong> Best for photographs and complex images</li>
                <li><strong>GIF:</strong> Supported for static images</li>
              </ul>

              <h4>Image Requirements</h4>
              <ul style={{ paddingLeft: '20px' }}>
                <li><strong>File Size:</strong> Maximum {MEDIA_CONSTANTS.MAX_FILE_SIZE_MB}MB per file</li>
                {validDimensions.length > 0 && (
                  <li><strong>Dimensions:</strong> {validDimensions.map(d => `${d.width}×${d.height}`).join(", ")}</li>
                )}
                <li><strong>Quality:</strong> High-resolution images recommended</li>
              </ul>

              <h4>Upload Tips</h4>
              <ul style={{ paddingLeft: '20px' }}>
                <li>Images are automatically validated during upload</li>
                <li>You can select multiple files at once for bulk upload (Ctrl/Cmd + Click)</li>
                <li>Invalid files will be highlighted with specific error messages</li>
                <li>Use PNG format for images with text or logos for best clarity</li>
              </ul>
            </>
          )}
        </div>
      </Modal>

      <Modal
        title={getModalTitle()}
        open={visible}
        onCancel={onCancel}
        footer={getFooter()}
        footerStyle={{ padding: '8px 12px', minHeight: 'auto' }}
        width={uploadState.mode === "bulk" ? 800 : 600}
        centered
      >
      {uploadState.mode === "single" ? (
        <Form form={form} onFinish={handleSingleUpload}>
          {renderContent()}

          {uploadState.previewUrl && (
            <Form.Item
              label={`${mediaType} Name`}
              name="name"
              rules={[
                {
                  required: true,
                  message: `Please enter ${mediaType.toLowerCase()} name`,
                },
              ]}
              style={{ marginTop: 16 }}
            >
              <Input placeholder={`Enter ${mediaType.toLowerCase()} name`} />
            </Form.Item>
          )}
        </Form>
      ) : (
        renderContent()
      )}
    </Modal>
    </>
  );
};

export default MediaUploadModal;
